import React from 'react'
import PropTypes from 'prop-types'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import { Pen, Trash, CopySimple, Bell } from '@phosphor-icons/react'
import { useLocation } from 'react-router-dom'

import Filter from '../Filter/Filter'
import SearchField from '../FormLegacy/FormFieldsV2/SearchField'

import InspectionsToolbarItem from './components/InspectionsToolbarItem'
import { getInspectionStatusFilterOptions, getInspectionFrequencyOptions } from './helpers'

const InternalInspectionsToolbar = ({
  onEdit = () => {},
  onDelete = () => {},
  onSearch = () => {},
  onDuplicate = () => {},
  onSendReminder = () => {},
  selectedRows = [],
  search,
  isLoading,
  componentOptions = {},
}) => {
  const { t } = useTranslation(['internal-inspections', 'common'])
  const location = useLocation()

  const currentPath = location.pathname.split('/').pop()
  let currentView = null

  const editButton = (
    <button
      disabled={!selectedRows.length || selectedRows.length > 1}
      onClick={onEdit}
      className="button button-black"
    >
      <Pen />
      {t('common:actions.edit')}
    </button>
  )

  const deleteButton = (
    <button disabled={!selectedRows.length} onClick={onDelete} className="button button-black">
      <Trash />
      {t('common:actions.delete')}
    </button>
  )

  const reminderButton = (
    <button
      disabled={!selectedRows.length}
      onClick={onSendReminder}
      className="button button-black"
    >
      <Bell />
      {t('internal-inspections:toolbar.sendReminder')}
    </button>
  )

  const duplicateButton = (
    <button disabled={!selectedRows.length} onClick={onDuplicate} className="button button-black">
      <CopySimple />
      {t('internal-inspections:toolbar.duplicate')}
    </button>
  )

  switch (currentPath) {
    case 'dashboard':
      currentView = (
        <InspectionsToolbarItem
          leftContent={
            <React.Fragment>
              <div className="ctp-internal-inspections-toolbar__filters">
                {getInspectionStatusFilterOptions(t).map((option) => (
                  <div
                    key={option.id}
                    className={`ctp-internal-inspections-toolbar__filters-item ${
                      componentOptions.currentStatus.value === option.value ? 'active' : ''
                    }`}
                    onClick={() => componentOptions.onChangeStatus(option)}
                  >
                    {option.text}
                  </div>
                ))}
              </div>
            </React.Fragment>
          }
          rightContent={
            <React.Fragment>
              {editButton}
              {deleteButton}
              {reminderButton}
              <DatePicker
                className="input"
                selectsRange
                isClearable
                placeholderText={t('internal-inspections:toolbar.selectTimeRangePlaceholder')}
                onChange={componentOptions.onChangeDateRange}
                startDate={componentOptions.currentRange.startDate}
                endDate={componentOptions.currentRange.endDate}
              />
              <div className="ctp-internal-inspections-toolbar__search">
                <SearchField
                  placeholder={t('internal-inspections:toolbar.searchDashboardPlaceholder')}
                  value={search}
                  onChange={onSearch}
                />
              </div>
            </React.Fragment>
          }
        />
      )
      break
    case 'recurring':
      currentView = (
        <InspectionsToolbarItem
          leftContent={null}
          rightContent={
            <React.Fragment>
              {editButton}
              {deleteButton}
              <Filter
                placeholder={componentOptions.currentFrequency.text}
                options={getInspectionFrequencyOptions(t)}
                disabled={isLoading}
                value={[componentOptions.currentFrequency]}
                variant="borderless"
                onChange={componentOptions.onChangeFrequency}
                additionalClass="ctp-internal-inspections-toolbar__filter"
                name="frequency"
              />
              <div className="ctp-internal-inspections-toolbar__search">
                <SearchField
                  placeholder={t('internal-inspections:toolbar.searchRecurringPlaceholder')}
                  value={search}
                  onChange={onSearch}
                />
              </div>
            </React.Fragment>
          }
        />
      )
      break
    case 'templates':
      currentView = (
        <InspectionsToolbarItem
          leftContent={null}
          rightContent={
            <React.Fragment>
              {editButton}
              {duplicateButton}
              {deleteButton}
              <div className="ctp-internal-inspections-toolbar__search">
                <SearchField
                  placeholder={t('internal-inspections:toolbar.searchTemplatesPlaceholder')}
                  value={search}
                  onChange={onSearch}
                />
              </div>
            </React.Fragment>
          }
        />
      )
      break
    default:
      break
  }

  return currentView
}

InternalInspectionsToolbar.propTypes = {
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSendReminder: PropTypes.func,
  onSearch: PropTypes.func,
  onDuplicate: PropTypes.func,
  selectedRows: PropTypes.array,
  search: PropTypes.string,
  isLoading: PropTypes.bool,
  componentOptions: PropTypes.shape({
    currentStatus: PropTypes.object,
    currentRange: PropTypes.object,
    currentFrequency: PropTypes.object,
    onChangeStatus: PropTypes.func,
    onChangeDateRange: PropTypes.func,
    onChangeFrequency: PropTypes.func,
  }),
}

export default InternalInspectionsToolbar
